import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { SegmentFacade } from '@segment/analytics-next/dist/types/lib/to-facade';
import * as sessionReplay from '@amplitude/session-replay-browser';
import {
  BehaviorSubject,
  filter,
  Observable,
  ReplaySubject,
  switchMap,
  take,
} from 'rxjs';
import { AnalyticsConfig } from '../analytics.config';

@Injectable({
  providedIn: 'root',
})
export class SegmentService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly ready$ = new BehaviorSubject<boolean>(false);
  private readonly analyticsBrowser$ = new ReplaySubject<AnalyticsBrowser>(1);
  private readonly sessionReplayInitialized$ = new ReplaySubject<void>(1);
  private amplitudeSessionReplayReturn: ReturnType<
    typeof sessionReplay.init
  > | null = null;
  private sessionId: number = this.getSessionId();
  private config!: AnalyticsConfig;

  private getSessionId(): number {
    // Note: This is temporary storage for the current browsing session only.
    // Amplitude manages sessions and creates new session IDs after 30 min of inactivity.
    // We sync with Amplitude's session ID via checkAndUpdateSessionId().
    if (!this.isBrowser) {
      return new Date().getTime();
    }

    const stored = localStorage.getItem('analytics_session_id');
    const sessionId = stored ? Number(stored) : new Date().getTime();
    if (!stored) {
      localStorage.setItem('analytics_session_id', sessionId.toString());
    }
    return sessionId;
  }

  async initialize(config: AnalyticsConfig): Promise<void> {
    this.config = config;

    const analyticsBrowser = AnalyticsBrowser.load(
      { writeKey: config.segmentWriteKey },
      { obfuscate: true },
    );

    this.analyticsBrowser$.next(analyticsBrowser);

    return new Promise<void>((resolve) => {
      analyticsBrowser.ready(() => {
        if (config.isSessionReplayEnabled) {
          this.configureMiddleware(analyticsBrowser);
        }
        this.ready$.next(true);
        resolve();
      });
    });
  }

  whenAnalyticsBrowserInitialized(): Observable<AnalyticsBrowser> {
    return this.ready$.pipe(
      filter((ready) => ready),
      switchMap(() => this.analyticsBrowser$),
      take(1),
    );
  }

  whenReady(): Observable<boolean> {
    return this.ready$.pipe(
      filter((ready) => ready),
      take(1),
    );
  }

  whenSessionReplayInitialized(): Observable<void> {
    return this.sessionReplayInitialized$.pipe(take(1));
  }

  async resetSession(): Promise<void> {
    const newSessionId = new Date().getTime();
    this.sessionId = newSessionId;

    if (this.isBrowser) {
      localStorage.setItem('analytics_session_id', newSessionId.toString());
    }

    if (this.amplitudeSessionReplayReturn) {
      await sessionReplay.setSessionId(newSessionId).promise;
    }
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    this.whenAnalyticsBrowserInitialized()
      .pipe(take(1))
      .subscribe((analyticsBrowser) => {
        analyticsBrowser.identify(userId, traits);
      });
  }

  private configureMiddleware(analyticsBrowser: AnalyticsBrowser): void {
    analyticsBrowser.addSourceMiddleware(({ payload, next }) => {
      // Use setTimeout to wait for next event loop to get session ID from payload
      // Ref: https://github.com/amplitude/Amplitude-TypeScript/issues/896#issuecomment-2618967056
      setTimeout(async () => {
        try {
          this.checkAndUpdateSessionId(payload);
          await this.initializeAmplitudeSessionReplay(analyticsBrowser);
          await sessionReplay.setSessionId(this.sessionId).promise;
        } catch {
          // Ignore until we have app insights and can log there.
        }
      });

      this.appendSessionReplayProperties(payload);
      next(payload);
    });
  }

  private checkAndUpdateSessionId(payload: SegmentFacade): void {
    const nextSessionId =
      (
        payload.obj.integrations?.['Actions Amplitude'] as {
          session_id: number;
        }
      )?.session_id || 0;

    if (nextSessionId > 0 && this.sessionId !== nextSessionId) {
      this.sessionId = nextSessionId;

      if (this.isBrowser) {
        localStorage.setItem('analytics_session_id', nextSessionId.toString());
      }
    }
  }

  private async initializeAmplitudeSessionReplay(
    analyticsBrowser: AnalyticsBrowser,
  ): Promise<void> {
    if (this.amplitudeSessionReplayReturn) {
      return;
    }

    const anonymousId =
      analyticsBrowser.instance?.user().anonymousId() ?? undefined;

    this.amplitudeSessionReplayReturn = sessionReplay.init(
      this.config.amplitudeSessionReplayKey,
      {
        sessionId: this.sessionId,
        deviceId: anonymousId,
        sampleRate: 1.0,
        logLevel: 1,
        useWebWorker: true,
        privacyConfig: {
          defaultMaskLevel: 'conservative',
          blockSelector: ['[data-private]'],
          maskSelector: ['[data-mask]'],
        },
      },
    );

    await this.amplitudeSessionReplayReturn.promise;
    this.sessionReplayInitialized$.next();
  }

  private appendSessionReplayProperties(payload: SegmentFacade): void {
    const payloadType = payload.type();
    if (payloadType === 'track' || payloadType === 'page') {
      const replayProperties = sessionReplay.getSessionReplayProperties() || {};

      payload.obj.properties = {
        ...payload.obj.properties,
        ...replayProperties,
      };
    }
  }
}
